import * as React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import get from 'lodash/get';
import set from 'lodash/set';
import { useDispatch } from 'react-redux';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import {
  BillingDetails,
  ErrorComponents,
  InfoPanel,
  InvoiceGroupSelectItem,
  OpenSidebarLink,
  SelectPromotionSelectItem,
  actions as SidebarActions,
  orderSuccessNotification,
  useFormSubmit,
  useModalDialog,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { BillingFrequency, Promotion } from '@css-xp/graphtypes';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { sendSuccessfulTimedMetric } from '@atlassian/temp-metal';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import {
  AddOnSelectItem,
  AddOns,
  PromotionInstance,
  UpdateOrderRequest,
  isPresent,
} from '@atlassian/cssxp-typescommon';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { SIDEBAR } from '../../../../entitlement-page/src/components/entitlement-page';
import { OfferingSelectItem } from '../../change-plan/src/entitlement-change-plan-pane';
import StatuspageAvailableOfferingsFormField from '../../entitlement-offerings/src/statuspage-available-offerings-form-field';
import { promotionAddModifier } from '../../utils/promotion-utils';
import './entitlement-annual-subscription-pane.scss';

interface Props {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountContext: TransactionAccountContextType;
}

interface FormData {
  invoiceGroupId?: InvoiceGroupSelectItem;
  offeringSelectedItem?: OfferingSelectItem;
  selectedAddOns?: {
    selectedAddOn: AddOnSelectItem;
    quantity: string;
  }[];
  promotionId?: SelectPromotionSelectItem;
  skipTrial?: boolean;
  customReason?: {
    value: string | undefined | null;
    label: string | undefined | null;
  };
  customValue?: number;
  customDuration?: {
    value: string | undefined | null;
    label: string | undefined | null;
  };
  customIterations?: number;
  offeringQuantity?: string;
}

enum ErrorCodeForReadableErrorMessage {
  UNAUTHORIZED = 403,
}

enum ReadableErrorMessage {
  UNAUTHORIZED_ERROR_MESSAGE = 'Missing relevant permission for the action.',
}

const ReadableErrorMap = [
  {
    status: ErrorCodeForReadableErrorMessage.UNAUTHORIZED,
    message: ReadableErrorMessage.UNAUTHORIZED_ERROR_MESSAGE,
  },
];

const constructRequestData = (entitlement: EntitlementDetails, formData: FormData, formApi: FormApi<FormData>) => {
  // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
  const transformedData: FormData = {};
  // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
  formApi.getRegisteredFields().forEach((key: any) => {
    // get the value from the field state or the form data
    const value = formApi.getFieldState(key)?.value ?? get(formData, key);
    set(transformedData, key, value);
  });
  const formattedAddOnSubscriptionRequests: AddOns[] =
    transformedData.selectedAddOns
      ?.map((addOn) => ({
        offeringId: addOn.selectedAddOn.value.offeringId || '',
        units: addOn.quantity,
      }))
      .filter(isPresent) || [];

  const requestData: UpdateOrderRequest = {
    invoiceGroupId: transformedData?.invoiceGroupId?.value,
    offeringId: transformedData?.offeringSelectedItem?.value.id ?? '',
    entitlementId: entitlement.id,
    billingFrequency: BillingFrequency.ANNUAL,
    addOns: formattedAddOnSubscriptionRequests,
    pricingPlanType: get(transformedData, 'pricingPlan'),
    skipTrial: isStatuspage(entitlement?.productId) ? undefined : Boolean(transformedData?.skipTrial),
    units: transformedData.offeringQuantity,
    productId: entitlement.productId ?? '', //@ts-ignore
    chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
  };

  requestData.promotionsToAdd = formData?.promotionId?.promotion?.id ? [formData.promotionId.promotion.id] : [];
  const promotionsToRemove =
    // @ts-ignore
    get(entitlement, 'promotions', [])?.map((promotion: Promotion) => promotion?.id as string) ||
    get(entitlement, 'scheduledChanges.promotions', [])?.map((promotion: Promotion) => promotion?.id as string) ||
    [];
  requestData.promotionsToRemove = promotionsToRemove;
  if (formData?.promotionId?.value) {
    requestData.addPromotions = promotionAddModifier({
      promotionSelectItem: formData.promotionId,
      customDuration: formData.customDuration,
      customReason: formData.customReason,
      customValue: formData.customValue,
      customIterations: formData.customIterations,
    });
  } else {
    const removePromotionInstance: PromotionInstance[] = [];
    promotionsToRemove.forEach((promo: string) => {
      const promotionInstance: PromotionInstance = {
        promotionDefinition: {
          promotionId: promo,
        },
      };
      removePromotionInstance.push(promotionInstance);
      requestData.removePromotions = removePromotionInstance;
    });
  }
  return requestData;
};

let startTime: number;
export const StatuspageAnnualSubscriptionPane = ({ entitlement, refetch, transactionAccountContext }: Props) => {
  const dispatch = useDispatch();
  const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<
    UpdateOrderRequest,
    any
  >({
    submitFormAction: updateEntitlementPlan,
    /* istanbul ignore next */
    onComplete: () => {
      calculateAndSendTimeMetric();
      dispatch(SidebarActions.closeSidebar());
      refetch();
    },
    readableErrorMap: ReadableErrorMap,
  });

  const calculateAndSendTimeMetric = () => {
    const presentTime = new Date().getTime();
    sendSuccessfulTimedMetric(
      'Switch to annual billing',
      SignalFxFEPerfStore.FE_USER_TASK_SWITCH_TO_ANNUAL_BILLING_TIME,
      presentTime - startTime
    );
  };

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { data, loading, error, getTransactionAccount } = transactionAccountContext;

  const { closeSidebar } = useSidebar();
  const { openModalDialog } = useModalDialog();

  if (loading) return <LoadingIndicator />;

  const transactionAccount = getTransactionAccount(data);
  if (error && !transactionAccount) return <ErrorComponents.GenericErrorPage error={error} />;
  if (error && transactionAccount) {
    // eslint-disable-next-line no-console
    console.error('Error on statuspage-annual-subscription-pane transactionAccount', error);
  }

  // TODO: form and new entitlement pages, handle null objects better
  if (!transactionAccount) {
    return null;
  }

  const scheduledBillingInvoiceGroupId = entitlement?.scheduledChanges?.scheduledBillingInvoiceGroupId;
  const activeBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;
  /* Check if entitlement has scheduledChanges than use scheduledBillingInvoiceGroupId or use the
   * invoiceGroup id from subscription
   */
  const defaultBillingGroupId = scheduledBillingInvoiceGroupId || activeBillingGroupId;

  const billToAddress = transactionAccount?.billToParty;
  // Display shipToAddress from the active invoice group associated for the entitlement
  const selectedInvoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes?.find(
    (invoiceGroup) => defaultBillingGroupId === invoiceGroup?.id
  );
  const shipToAddress = selectedInvoiceGroup?.shipToParty;

  const handleFormSubmit = (formData: FormData, formApi: FormApi<FormData>) => {
    startTime = new Date().getTime();
    const requestData = constructRequestData(entitlement, formData, formApi);
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const transitionTimeError =
      response.transitionTime === 'unknown'
        ? ' - We were unable to determine if the successfully submitted order is immediate or scheduled.'
        : undefined;

    const notificationOptions = orderSuccessNotification(
      entitlement?.billingFrequency === BillingFrequency.MONTHLY
        ? 'SWITCH_MONTHLY_TO_ANNUAL'
        : 'SWITCH_TRAIL_TO_ANNUAL',
      response.transitionTime,
      transitionTimeError,
      true
    );

    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const hasPaymentMethod = transactionAccount?.availablePaymentMethods?.totalCount ?? false;

  return (
    <>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title="Switch to annual billing"></FormHeader>
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to switch trial to annual subscription for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!hasPaymentMethod && (
                <InfoPanel
                  type="warning"
                  title="Cannot switch to annual billing - entitlement has no billing details on file"
                  message={
                    <span>
                      This entitlement cannot be switched to annual billing until there are billing details on file.{' '}
                      {
                        <OpenSidebarLink
                          sidebar={SIDEBAR.GET_UPDATE_PAYMENT_METHOD_LINK}
                          payload={{
                            updatePaymentMethodLink: entitlement?.updatePaymentMethodLink,
                          }}
                          label="Get update payment link"
                        >
                          Get update payment link
                        </OpenSidebarLink>
                      }{' '}
                      and ask the customer to add their credit card before processing this change.
                    </span>
                  }
                />
              )}
            </FormSection>
            <FormSection title="Billing details">
              <div style={{ maxWidth: '600px' }}>
                <BillingDetails
                  billToAddress={billToAddress}
                  shipToAddress={shipToAddress}
                  isEditable={false}
                  openBillingModal={openModalDialog}
                  shouldDisplayEditOption={false}
                />
              </div>
            </FormSection>
            <FormSection title="Product details">
              <StatuspageAvailableOfferingsFormField
                entitlement={entitlement}
                transactionAccount={transactionAccount}
                ispricingPlanEnabled={true}
                isAnnual={!(entitlement?.billingFrequency === BillingFrequency.ANNUAL)}
              />
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} testId="submit-to-annual">
                  Switch to annual
                </LoadingButton>
                <Button appearance="subtle" onClick={() => closeSidebar()}>
                  Cancel
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </>
  );
};
